import React, { useEffect, useState } from 'react';
import './styles.scss';

// Personal debug panel, toggled by "/debug" in chat (constructorComponents/chat/index.js dispatches
// 'skymp5-front:togglePersonalDebug'). Fed by skymp5-client's personalDebugService.ts while open.
// It only reports the player's own client and scenery, never another character's id or name.

interface NamedId {
  id: string;
  name: string;
}

interface Target {
  kind: 'actor' | 'object';
  name: string;
  refDesc: string | null;
  baseDesc: string | null;
}

interface Snapshot {
  pos: number[];
  cell: NamedId | null;
  world: NamedId | null;
  fps: number;
  clockOffsetMs: number | null;
  target: Target | null;
}

// A PC clock further than this from the server is worth flagging (it skews the in-game time of day)
const CLOCK_WARN_MS = 5 * 60 * 1000;

const send = (message: string): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(message);
  } catch (e) {
    // Outside the game (Storybook, plain browser): nothing to tell
  }
};

const copyToClipboard = (value: string): boolean => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).catch(() => { /* fall through to the textarea trick */ });
    }
  } catch (e) { /* ignore */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    return false;
  }
};

const named = (n: NamedId | null): string => (n ? `${n.name || '(sans nom)'} [${n.id}]` : '-');

const formatClock = (ms: number | null): string => {
  if (ms === null) return 'pas encore reçue du serveur';
  const sign = ms >= 0 ? '+' : '-';
  return `${sign}${(Math.abs(ms) / 1000).toFixed(1)} s`;
};

const describeTarget = (t: Target | null): string => {
  if (!t) return '-';
  if (t.kind === 'actor') return t.name;
  return `${t.name} (réf ${t.refDesc || '?'}, base ${t.baseDesc || '?'})`;
};

// What the Copy button puts on the clipboard, one fact per line
export const buildReport = (s: Snapshot): string => [
  `Position : ${s.pos.join(' ')}`,
  `Cellule : ${named(s.cell)}`,
  `Espace : ${named(s.world)}`,
  `Cible : ${describeTarget(s.target)}`,
  `FPS : ${s.fps}`,
  `Écart d'horloge PC/serveur : ${formatClock(s.clockOffsetMs)}`,
].join('\n');

const PersonalDebug: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const toggle = () => setOpen((was) => !was);
    const update = (e: Event) => setSnapshot((e as CustomEvent).detail as Snapshot);
    window.addEventListener('skymp5-front:togglePersonalDebug', toggle);
    window.addEventListener('skymp5-client:personalDebug', update);
    return () => {
      window.removeEventListener('skymp5-front:togglePersonalDebug', toggle);
      window.removeEventListener('skymp5-client:personalDebug', update);
    };
  }, []);

  // The client only samples (and pushes) while the panel is open
  useEffect(() => {
    send(open ? 'debug-panel:open' : 'debug-panel:close');
    if (!open) setSnapshot(null);
  }, [open]);

  if (!open) return null;

  const clockOff = snapshot?.clockOffsetMs != null && Math.abs(snapshot.clockOffsetMs) > CLOCK_WARN_MS;

  return (
    <div className="personal-debug">
      <div className="personal-debug__title">Debug personnel</div>
      {!snapshot ? (
        <div className="personal-debug__row">Chargement...</div>
      ) : (
        <>
          <div className="personal-debug__row"><span>Position</span><span>{snapshot.pos.join('  ')}</span></div>
          <div className="personal-debug__row"><span>Cellule</span><span>{named(snapshot.cell)}</span></div>
          <div className="personal-debug__row"><span>Espace</span><span>{named(snapshot.world)}</span></div>
          <div className="personal-debug__row"><span>Cible</span><span>{describeTarget(snapshot.target)}</span></div>
          <div className="personal-debug__row"><span>FPS</span><span>{snapshot.fps}</span></div>
          <div className={'personal-debug__row' + (clockOff ? ' personal-debug__row--warn' : '')}>
            <span>Horloge PC</span>
            <span>{formatClock(snapshot.clockOffsetMs)}{clockOff ? ' (décalée)' : ''}</span>
          </div>
        </>
      )}
      <div className="personal-debug__buttons">
        <button
          type="button"
          disabled={!snapshot}
          onClick={() => {
            if (snapshot && copyToClipboard(buildReport(snapshot))) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
        >
          {copied ? 'Copié' : 'Copier'}
        </button>
        <button type="button" onClick={() => setOpen(false)}>Fermer</button>
      </div>
    </div>
  );
};

export default PersonalDebug;
